import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "TypeScript for serverless lambda backends 👌",
  "description": "",
  "published": "2019-05-27T00:00:00.000Z",
  "redirect_from": ["/blog/typescript-serverless-lambda/swizec/9103"],
  "categories": "Back End, Technical",
  "hero": "./img/techletter.app-screenshot-1558976558203.png",
  "content_upgrade": "ServerlessHandbook"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`A few months ago I inherited a Kotlin project. It was an unfinished prototype and all I had to do was wrap it up and deploy.`}</p>
    <p>{`How hard can it be?`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`The time has come for me to learn Kotlin. Because reasons`}<br parentName="p"></br><br parentName="p"></br>{`First surprise: It takes 700MB of space just to install the language. No IDE or anything.`}<br parentName="p"></br><br parentName="p"></br>{`node_modules ain't got nothing on this`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1103070682248704002"
      }}>{`March 5, 2019`}</a></blockquote>
    <p>{`Weeks passed. Even months.`}</p>
    <p>{`Every time I touched the project, it broke. Nothing worked. Wouldn't run on my machine, wouldn't read API requests, couldn't get tests working, if I changed some code I broke it all.`}</p>
    <p>{`That's when I realized `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/learning-a-new-field-is-super-hard-i-get-it-now-%F0%9F%98%85/swizec/8974"
      }}>{`Learning a new field is super hard`}</a>{` 😅`}</p>
    <ContentUpgrades.ServerlessHandbook />
    <p>{`Tried to look for help online and ... nothing. The Kotlin community just doesn't seem to exist. There's no gaggle of blogs, no quality courses, there's a few books but `}<em parentName="p">{`everything is for Android`}</em>{`.`}</p>
    <p>{`I'm using Kotlin for the backend damn it, why do I feel so alone?`}</p>
    <p>{`With no community to be found there's only one conclusion 👉 I'm forcing Kotlin to do something it doesn't want to do. Or Kotlin is weird and on its way out.`}</p>
    <p>{`Hell, it was easier to find LOLCODE resources for that `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/lolcode-to-javascript-compiler-babel-macro/swizec/9083"
      }}>{`LOLCODE-to-JavaScript compiler`}</a>{` the other day ...`}</p>
    <p><img parentName="p" {...{
        "src": "/1006389a11cdded10b4c50c82efb0701/media-n4aziifHh5BrSCMZwz-giphy.gif",
        "alt": "screw_this giphy"
      }}></img></p>
    <p>{`So I said screw it and got the whole project working in TypeScript in 3 days. ✌️`}</p>
    <h2 {...{
      "id": "why-kotlin-why-typescript"
    }}>{`Why Kotlin? Why TypeScript?`}</h2>
    <p>{`A good question at this point: `}<em parentName="p">{`Why did we even try Kotlin in the first place?`}</em></p>
    <p>{`There's a lot of backstory, but it's all about solving these problems 👇`}</p>
    <ul>
      <li parentName="ul">{`we have a Ruby on Rails backend`}</li>
      <li parentName="ul">{`it is slow`}</li>
      <li parentName="ul">{`parts of the system are struggling under load`}</li>
      <li parentName="ul">{`dynamic typing leads to bugs`}</li>
    </ul>
    <p>{`We wanted to extract the most impacted subsystem. Build it so that it's fast, reliable, and easy for many developers to work with. And isolated from the rest of our monolith.`}</p>
    <p>{`You can get speed and reliability with AWS Lambdas. You can get type safety with Java. And Java is a `}<em parentName="p">{`very`}</em>{` popular language on the backend.`}</p>
    <p>{`But Java is hard to use and annoying to write.`}</p>
    <p>{`Kotlin seemed like a great alternative. Runs on the Java Virtual Machine (JVM), interops with Java, and supports many modern functional idioms. Plus it's fast because the JVM is fast.`}</p>
    <p>{`Except nobody on the team knows Kotlin.`}</p>
    <p>{`😅`}</p>
    <p>{`But we're smart, we can learn this! And we sort of did.`}</p>
    <p>{`Until both the person leading the lets-move-to-JVM charge left the company and his I-love-doing-backend protege left us for a career at Google.`}</p>
    <p>{`Backend people are a special breed, they all seem to love Java and Java-like things so much.`}</p>
    <p><img parentName="p" {...{
        "src": "/ba5f8f221886c121e9eeef90c763e9d1/media-13Qumr2SLqrl5e-giphy.gif",
        "alt": "shrug giphy"
      }}></img></p>
    <p>{`So here I was with this unfinished prototype, no skills in Kotlin, a deep seated distrust of all things Java from college, and an open deadline. Get it working when you can, nothing's on fire right now.`}</p>
    <p>{`I tried. I did my best. I gave up.`}</p>
    <h3 {...{
      "id": "typescript"
    }}>{`TypeScript!`}</h3>
    <p>{`TypeScript solves our problems, works `}<em parentName="p">{`great`}</em>{` with AWS Lambdas, and the whole team knows at least JavaScript.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`So I finally forced myself to write some TypeScript for real and I don't hate it. 🤨`}<br parentName="p"></br><br parentName="p"></br>{`Greenfield backend project so I was able to avoid \`any\` like the plague. That helped a bunch in making TS make sense.`}<br parentName="p"></br><br parentName="p"></br>{`Here's things I liked and things I didn't 👇`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1132156516289482752"
      }}>{`May 25, 2019`}</a></blockquote>
    <p>{`TypeScript with AWS Lambda 👇`}</p>
    <ul>
      <li parentName="ul">{`super fast because your code is small`}</li>
      <li parentName="ul">{`faster cold start times than Kotlin`}</li>
      <li parentName="ul">{`type safety for robustness`}</li>
    </ul>
    <p>{`You see the thing with AWS Lambda is that you are charged for time. `}<a parentName="p" {...{
        "href": "https://medium.com/@jconning/aws-lambda-faster-is-cheaper-6bf32f58d741"
      }}>{`Faster is cheaper`}</a>{`, and while JVM functions `}<em parentName="p">{`run`}</em>{` faster, node functions `}<a parentName="p" {...{
        "href": "https://medium.com/@nathan.malishev/lambda-cold-starts-language-comparison-%EF%B8%8F-a4f4b5f16a62"
      }}><em parentName="a">{`start`}</em>{` faster`}</a>{`.`}</p>
    <p>{`Our functions are small so runtimes are small. But those cold starts can add up since `}<a parentName="p" {...{
        "href": "https://hackernoon.com/im-afraid-you-re-thinking-about-aws-lambda-cold-starts-all-wrong-7d907f278a4f"
      }}>{`most Lambda calls are cold`}</a>{`. It's a new computer almost every time :)`}</p>
    <p>{`Engineering is all about tradeoffs`}</p>
    <h2 {...{
      "id": "how-to-use-typescript-for-aws-lambdas"
    }}>{`How to use TypeScript for AWS Lambdas`}</h2>
    <p>{`So how do you use TypeScript to write an AWS Lambda function?`}</p>
    <p>{`Let's write a `}<inlineCode parentName="p">{`Hello World`}</inlineCode>{` function. I'm extracting this from my real code and haven't tested it. Think of this code as a guide, not a copypasta.`}</p>
    <p><img parentName="p" {...{
        "src": "/0a2974d69e528ac3c400d96244c84e91/media-Znz2cbofYIlMc-giphy.gif",
        "alt": "pirate_code giphy"
      }}></img></p>
    <p>{`You start by installing the TypeScript compiler. You'll need node support too.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=null&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%24%20npm%20install%20-g%20typescript%20ts-node"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "382px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "48.87892376681614%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'196\\'%20viewBox=\\'0%200%20400%20196\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M36%2053c-3%202-3%2090-1%2092%203%202%2034%203%20166%203%20131%200%20162-1%20165-3%202-2%202-90-1-92-2-3-327-3-329%200m37%2016c-6%206%203%2013%208%208l2-4c0-5-7-8-10-4m21%200c-5%206%202%2013%207%208%204-3%204-5%201-8s-6-3-8%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d9a88f5592875dd93e6ad3a13aac5b6e/ca0a1/techletter.app-screenshot-1558976558203.webp 223w", "/static/d9a88f5592875dd93e6ad3a13aac5b6e/c5024/techletter.app-screenshot-1558976558203.webp 382w"],
                "sizes": "(max-width: 382px) 100vw, 382px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/d9a88f5592875dd93e6ad3a13aac5b6e/e92b6/techletter.app-screenshot-1558976558203.png 223w", "/static/d9a88f5592875dd93e6ad3a13aac5b6e/77edc/techletter.app-screenshot-1558976558203.png 382w"],
                "sizes": "(max-width: 382px) 100vw, 382px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/d9a88f5592875dd93e6ad3a13aac5b6e/77edc/techletter.app-screenshot-1558976558203.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`You can now write TypeScript programs and compile them with `}<inlineCode parentName="p">{`tsc`}</inlineCode>{` so node can run them. Yes you'll need to compile every time before running.`}</p>
    <p>{`But you already do that for your frontend code anyway :)`}</p>
    <p>{`You're also going to need the Serverless framework.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=null&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%24%20npm%20install%20-g%20serverless"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "318px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "58.744394618834086%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'235\\'%20viewBox=\\'0%200%20400%20235\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M43%2063c-2%202-2%209-2%2056l1%2055c2%203%203%203%20159%203%20155%200%20156%200%20158-3a1061%201061%200%2000-1-111l-1-2H44l-1%202m44%2019c-3%203-3%207%200%2011%204%203%207%203%2010-1%204-3%204-6%201-10-4-3-8-3-11%200m26%201c-4%203-4%205-1%208%204%205%2011%203%2012-3%201-1%200-3-2-5-4-4-5-4-9%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/3caca8b7f061f74bb47ceec37680bfe2/ca0a1/techletter.app-screenshot-1558976558269.webp 223w", "/static/3caca8b7f061f74bb47ceec37680bfe2/4a32d/techletter.app-screenshot-1558976558269.webp 318w"],
                "sizes": "(max-width: 318px) 100vw, 318px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/3caca8b7f061f74bb47ceec37680bfe2/e92b6/techletter.app-screenshot-1558976558269.png 223w", "/static/3caca8b7f061f74bb47ceec37680bfe2/cd2d9/techletter.app-screenshot-1558976558269.png 318w"],
                "sizes": "(max-width: 318px) 100vw, 318px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/3caca8b7f061f74bb47ceec37680bfe2/cd2d9/techletter.app-screenshot-1558976558269.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`At this point it's a good idea to open a new terminal window so the new CLI commands register.`}</p>
    <p>{`Oh and you should `}<a parentName="p" {...{
        "href": "https://serverless.com/framework/docs/providers/aws/guide/credentials/"
      }}>{`setup serverless AWS credentials`}</a>{`. It's a little tedious, but you only gotta do it once for all your projects.`}</p>
    <h3 {...{
      "id": "tsconfigjson"
    }}>{`tsconfig.json`}</h3>
    <p>{`Before you can seamlessly compile the whole project, you need to configure TypeScript. I copypasta'd this JSON from a blogpost and it works great. Sensible defaults I'd say 😇`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=json&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%7B%0A%09%22compilerOptions%22%3A%20%7B%0A%09%09%22target%22%3A%20%22es2017%22%2C%0A%09%09%22module%22%3A%20%22commonjs%22%2C%0A%09%09%22outDir%22%3A%20%22.%2Fdist%22%2C%0A%09%09%22strict%22%3A%20true%2C%0A%09%09%22baseUrl%22%3A%20%22.%2F%22%2C%0A%09%09%22typeRoots%22%3A%20%5B%0A%09%09%09%22node_modules%2F%40types%22%0A%09%09%5D%2C%0A%09%09%22types%22%3A%20%5B%0A%09%09%09%22node%22%0A%09%09%5D%2C%0A%09%09%22esModuleInterop%22%3A%20true%2C%0A%09%09%22inlineSourceMap%22%3A%20true%0A%09%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "366px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "129.59641255605382%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'519\\'%20viewBox=\\'0%200%20400%20519\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M38%2054c-3%202-3%206-3%20208l1%20204c3%203%2010%203%20165%203%20154%200%20161%200%20164-3l1-204c0-202%200-206-3-208H38m39%2017c-3%203-4%207-1%209%206%206%2014-2%209-8-2-3-5-3-8-1m21%201c-3%203-2%206%200%208%204%204%2010%201%2010-4%200-6-7-8-10-4\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/959c0a1685321c2d3eceebcd7ea45313/ca0a1/techletter.app-screenshot-1558976558093.webp 223w", "/static/959c0a1685321c2d3eceebcd7ea45313/4146f/techletter.app-screenshot-1558976558093.webp 366w"],
                "sizes": "(max-width: 366px) 100vw, 366px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/959c0a1685321c2d3eceebcd7ea45313/e92b6/techletter.app-screenshot-1558976558093.png 223w", "/static/959c0a1685321c2d3eceebcd7ea45313/8b153/techletter.app-screenshot-1558976558093.png 366w"],
                "sizes": "(max-width: 366px) 100vw, 366px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/959c0a1685321c2d3eceebcd7ea45313/8b153/techletter.app-screenshot-1558976558093.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`This tells the TypeScript compiler that you're targeting `}<inlineCode parentName="p">{`es2017`}</inlineCode>{` – the latest JavaScript standard supported by Node 8.10, which is the latest Node that AWS supports. You're using `}<inlineCode parentName="p">{`commonjs`}</inlineCode>{` modules, writing compiled files to `}<inlineCode parentName="p">{`./dist`}</inlineCode>{` and so on.`}</p>
    <p>{`tsconfig.json supports many options but you aren't likely to need them. At least not at first.`}</p>
    <h3 {...{
      "id": "the-packagejson"
    }}>{`The package.json`}</h3>
    <p>{`Next we'll need a package.json file with some scripts. These will help you build and deploy your code.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=json&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%7B%0A%09%22name%22%3A%20%22hello-world-lambda%22%2C%0A%09%22scripts%22%3A%20%7B%0A%09%09%22build%22%3A%20%22tsc%22%2C%0A%09%09%22deploy%22%3A%20%22npm%20run%20build%20%26%26%20sls%20deploy%22%0A%09%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "478px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "61.88340807174888%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'247\\'%20viewBox=\\'0%200%20400%20247\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M29%2041c-3%202-3%20164%200%20166h342c2-1%202-3%202-83s0-82-2-83H29\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5ebbe632871a6be6cf54520dc336dc5d/ca0a1/techletter.app-screenshot-1558976558076.webp 223w", "/static/5ebbe632871a6be6cf54520dc336dc5d/75680/techletter.app-screenshot-1558976558076.webp 445w", "/static/5ebbe632871a6be6cf54520dc336dc5d/2b263/techletter.app-screenshot-1558976558076.webp 478w"],
                "sizes": "(max-width: 478px) 100vw, 478px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/5ebbe632871a6be6cf54520dc336dc5d/e92b6/techletter.app-screenshot-1558976558076.png 223w", "/static/5ebbe632871a6be6cf54520dc336dc5d/e66bf/techletter.app-screenshot-1558976558076.png 445w", "/static/5ebbe632871a6be6cf54520dc336dc5d/50978/techletter.app-screenshot-1558976558076.png 478w"],
                "sizes": "(max-width: 478px) 100vw, 478px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/5ebbe632871a6be6cf54520dc336dc5d/50978/techletter.app-screenshot-1558976558076.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`We run `}<inlineCode parentName="p">{`tsc`}</inlineCode>{` to build the project, and `}<inlineCode parentName="p">{`sls deploy`}</inlineCode>{` to deploy. Adding the build step to your `}<inlineCode parentName="p">{`deploy`}</inlineCode>{` script makes it less likely that you'll forget to build before deploying.`}</p>
    <p>{`Always important to deploy the latest code. Gets very frustrating if you don't 😅`}</p>
    <h3 {...{
      "id": "serverlessyml"
    }}>{`serverless.yml`}</h3>
    <p>{`We use `}<inlineCode parentName="p">{`serverless.yml`}</inlineCode>{` to configure the Serverless framework. Using this file means you:`}</p>
    <ul>
      <li parentName="ul">{`never have to deal with the UI on AWS (or Azure or whatever)`}</li>
      <li parentName="ul">{`keep your system architecture in source control`}</li>
      <li parentName="ul">{`can reliably build the same server environment every time`}</li>
    </ul>
    <p>{`Might seem weird at first, but once you get used to all your infrastructure being part of your code ... wow. It's like a whole new world.`}</p>
    <p>{`Our `}<inlineCode parentName="p">{`Hello World`}</inlineCode>{` example doesn't need much 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=yaml&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=service%3A%20hello-world%0A%0Aprovider%3A%0A%09name%3A%20aws%0A%09runtime%3A%20nodejs8.10%0A%09stage%3A%20dev%0A%09%0A%09functions%3A%0A%09%09hello%3A%0A%09%09%09handler%3A%20dist%2Fhello.handler%0A%09%09%09events%3A%0A%09%09%09%09-%20http%3A%0A%09%09%09%09%09%09path%3A%20hello%0A%09%09%09%09%09%09method%3A%20GET%0A%09%09%09%09%09%09cors%3A%20true"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "414px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "105.82959641255604%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'424\\'%20viewBox=\\'0%200%20400%20424\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M33%2048c-3%203-3%20327%200%20329%203%203%20332%203%20334%200%203-3%203-327%200-329-3-3-332-3-334%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6a425a56ba934de169751dd5a28277b9/ca0a1/techletter.app-screenshot-1558976558127.webp 223w", "/static/6a425a56ba934de169751dd5a28277b9/3ca80/techletter.app-screenshot-1558976558127.webp 414w"],
                "sizes": "(max-width: 414px) 100vw, 414px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/6a425a56ba934de169751dd5a28277b9/e92b6/techletter.app-screenshot-1558976558127.png 223w", "/static/6a425a56ba934de169751dd5a28277b9/b910a/techletter.app-screenshot-1558976558127.png 414w"],
                "sizes": "(max-width: 414px) 100vw, 414px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/6a425a56ba934de169751dd5a28277b9/b910a/techletter.app-screenshot-1558976558127.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`With that you've configured:`}</p>
    <ul>
      <li parentName="ul">{`an AWS Lambda function called `}<inlineCode parentName="li">{`hello`}</inlineCode></li>
      <li parentName="ul">{`an APIGateway to proxy API requests`}</li>
      <li parentName="ul">{`a CloudWatch setup for any logs`}</li>
    </ul>
    <p>{`Yep. All that in those few lines of Yaml. Once you deploy Serverless will give you the URL for your hello function.`}</p>
    <p>{`But first you gotta write it`}</p>
    <h3 {...{
      "id": "the-hello-function--srchellots"
    }}>{`The hello function – src/hello.ts`}</h3>
    <p>{`Before you write the function, there's a couple dependencies I'd recommend. They make TypeScript work smoother with node and AWS.`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=null&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%24%20npm%20install%20--save%20%40types%2Faws-lambda%20%40types%2Fnode%20typescript"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "590px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "31.838565022421527%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'127\\'%20viewBox=\\'0%200%20400%20127\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M22%2034v31l1%2030h355V34l-178-1-178%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/88854d332cec7af2ae53cd1c63b0a38e/ca0a1/techletter.app-screenshot-1558976558028.webp 223w", "/static/88854d332cec7af2ae53cd1c63b0a38e/75680/techletter.app-screenshot-1558976558028.webp 445w", "/static/88854d332cec7af2ae53cd1c63b0a38e/5ca24/techletter.app-screenshot-1558976558028.webp 590w"],
                "sizes": "(max-width: 590px) 100vw, 590px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/88854d332cec7af2ae53cd1c63b0a38e/e92b6/techletter.app-screenshot-1558976558028.png 223w", "/static/88854d332cec7af2ae53cd1c63b0a38e/e66bf/techletter.app-screenshot-1558976558028.png 445w", "/static/88854d332cec7af2ae53cd1c63b0a38e/fcda8/techletter.app-screenshot-1558976558028.png 590w"],
                "sizes": "(max-width: 590px) 100vw, 590px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/88854d332cec7af2ae53cd1c63b0a38e/fcda8/techletter.app-screenshot-1558976558028.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`I'm not sure the `}<inlineCode parentName="p">{`typescript`}</inlineCode>{` dependency is strictly necessary, but I have it. The two `}<inlineCode parentName="p">{`@types`}</inlineCode>{` dependencies give you type information for built-in stuff.`}</p>
    <p>{`Your hello world function looks like this 👇`}</p>
    <p><a parentName="p" {...{
        "href": "https://carbon.now.sh/?bg=rgba(255,255,255,1)&t=seti&l=typescript&ds=true&wc=true&wa=true&pv=48px&ph=32px&ln=false&code=%2F%2F%20src%2Fhello.ts%0A%0Aimport%20%7B%20APIGatewayEvent%20%7D%20from%20%22aws-lambda%22%3B%0A%0Aexport%20const%20handler%20%3D%20async%20(event%3A%20APIGatewayEvent)%3A%20Promise%3Cany%3E%20%3D%3E%20%7B%0A%09return%20%7B%0A%09%09statusCode%3A%20200%2C%0A%09%09body%3A%20%22Hello%20world%22%0A%09%7D%0A%7D"
      }}><figure parentName="a" {...{
          "className": "gatsby-resp-image-figure",
          "style": {
            "textAlign": "center",
            "fontStyle": "italic"
          }
        }}>{`
    `}<span parentName="figure" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "678px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "51.569506726457405%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'206\\'%20viewBox=\\'0%200%20400%20206\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M20%2029l-1%2075%201%2074h361v-74l-1-75c-2-2-358-2-360%200\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/48755528b2598e84b935d507238097dd/ca0a1/techletter.app-screenshot-1558976558215.webp 223w", "/static/48755528b2598e84b935d507238097dd/75680/techletter.app-screenshot-1558976558215.webp 445w", "/static/48755528b2598e84b935d507238097dd/7e03f/techletter.app-screenshot-1558976558215.webp 678w"],
                "sizes": "(max-width: 678px) 100vw, 678px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/48755528b2598e84b935d507238097dd/e92b6/techletter.app-screenshot-1558976558215.png 223w", "/static/48755528b2598e84b935d507238097dd/e66bf/techletter.app-screenshot-1558976558215.png 445w", "/static/48755528b2598e84b935d507238097dd/38cea/techletter.app-screenshot-1558976558215.png 678w"],
                "sizes": "(max-width: 678px) 100vw, 678px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/48755528b2598e84b935d507238097dd/38cea/techletter.app-screenshot-1558976558215.png",
                "alt": "Click through for source",
                "title": "Click through for source",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
            "className": "gatsby-resp-image-figcaption"
          }}>{`Click through for source`}</figcaption>{`
  `}</figure></a></p>
    <p>{`And that's it.`}</p>
    <p>{`You're importing the type definition for the `}<inlineCode parentName="p">{`APIGatewayEvent`}</inlineCode>{`. That specifies which properties it does and doesn't have, gives you code completion, and helpful tooltips in VS Code.`}</p>
    <p>{`To make an endpoint, you export a function. Often called `}<inlineCode parentName="p">{`handler`}</inlineCode>{` but can be anything. You specified the exact name in your serverless.yml earlier (`}<inlineCode parentName="p">{`handler: dist/hello.handler`}</inlineCode>{`).`}</p>
    <p>{`This function takes an `}<inlineCode parentName="p">{`event`}</inlineCode>{`, does whatever it wants, and returns a response with a `}<inlineCode parentName="p">{`statusCode`}</inlineCode>{` and a `}<inlineCode parentName="p">{`body`}</inlineCode>{`. 200 means `}<inlineCode parentName="p">{`OK`}</inlineCode>{`.`}</p>
    <p>{`You can run `}<inlineCode parentName="p">{`npm run deploy`}</inlineCode>{`, wait a bit, and voila you've got a URL that says `}<inlineCode parentName="p">{`Hello World`}</inlineCode>{` 👌`}</p>
    <p>{`That's what I love about this new function-as-a-service world. `}<em parentName="p">{`Anyone`}</em>{` can build a backend. A `}<em parentName="p">{`good`}</em>{` backend. Not just those Java-toting backend weirdos.`}</p>
    <p>{`Happy Monday ❤️,`}<br parentName="p"></br>{`
`}{`~Swizec`}</p>
    <p>{`PS: if you wanna read more about my initial impressions of TypeScript, you should check out `}<a parentName="p" {...{
        "href": "https://twitter.com/Swizec/status/1132156516289482752"
      }}>{`this twitter thread`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      